import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { Film } from '../types'
import { mockFilms } from '../data/mockFilms'

// Maps a row from the `movies` table to our Film type
function mapRowToFilm(row: any): Film {
  let formattedDate = ''
  if (row.release_date) {
    try {
      const d = new Date(row.release_date)
      formattedDate = d.toLocaleDateString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    } catch {
      formattedDate = ''
    }
  }

  const fullDesc = row.description ?? ''
  const excerpt =
    fullDesc.length > 150 ? fullDesc.slice(0, 150).trimEnd() + '…' : fullDesc

  return {
    id: String(row.id ?? ''),
    title: row.title ?? '',
    year: row.year ?? 0,
    posterUrl: row.poster_url ?? undefined,
    stillUrls: row.poster_url ? [row.poster_url] : [],
    director: Array.isArray(row.directors)
      ? row.directors.join(', ')
      : (row.directors ?? ''),
    writers: [],
    cast: row.cast_list ?? [],
    genreTags: row.genres ?? [],
    ageCategory: '',
    studio: '',
    republic: Array.isArray(row.countries)
      ? row.countries.join(', ')
      : (row.countries ?? ''),
    color: '',
    meters: undefined,
    duration: row.duration_min ?? undefined,
    releaseDateVE: formattedDate,
    catalogNumber: row.kp_id ?? '',
    descriptionExcerpt: excerpt,
    descriptionFull: fullDesc,
    ratingKp: row.rating_kp != null ? Number(row.rating_kp) : undefined,
    ratingImdb: row.rating_imdb != null ? Number(row.rating_imdb) : undefined,
    kpId: row.kp_id ?? undefined,
    imdbId: row.imdb_id ?? undefined,
  }
}

// Simple shuffle for "random" default order
function shuffleArray<T>(arr: T[]): T[] {
  const shuffled = [...arr]
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
  }
  return shuffled
}

// ─── Filter types ───
export interface CatalogFilters {
  genres: string[]
  yearFrom: number
  yearTo: number
  search: string
  ageCategories: string[]
  studios: string[]
}

export const DEFAULT_FILTERS: CatalogFilters = {
  genres: [],
  yearFrom: 1920,
  yearTo: 2000,
  search: '',
  ageCategories: [],
  studios: [],
}

const PAGE_SIZE = 100

// ─── Catalog hook with pagination, filtering, sorting ───
export function useCatalogFilms(
  page: number,
  filters: CatalogFilters,
  sortBy: string,
) {
  const [films, setFilms] = useState<Film[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [totalCount, setTotalCount] = useState(0)

  useEffect(() => {
    let cancelled = false

    async function fetchFilms() {
      try {
        setLoading(true)
        setError(null)

        // Build count query
        let countQuery = supabase
          .from('movies')
          .select('*', { count: 'exact', head: true })

        // Build data query
        let dataQuery = supabase.from('movies').select('*')

        // Apply filters to both queries
        if (filters.search.trim()) {
          const searchTerm = `%${filters.search.trim()}%`
          countQuery = countQuery.ilike('title', searchTerm)
          dataQuery = dataQuery.ilike('title', searchTerm)
        }

        if (filters.yearFrom > 1920) {
          countQuery = countQuery.gte('year', filters.yearFrom)
          dataQuery = dataQuery.gte('year', filters.yearFrom)
        }

        if (filters.yearTo < 2000) {
          countQuery = countQuery.lte('year', filters.yearTo)
          dataQuery = dataQuery.lte('year', filters.yearTo)
        }

        if (filters.genres.length > 0) {
          // overlaps = any of the selected genres match
          countQuery = countQuery.overlaps('genres', filters.genres)
          dataQuery = dataQuery.overlaps('genres', filters.genres)
        }

        // Apply sorting
        switch (sortBy) {
          case 'year_desc':
            dataQuery = dataQuery.order('year', { ascending: false })
            break
          case 'year_asc':
            dataQuery = dataQuery.order('year', { ascending: true })
            break
          case 'title':
            dataQuery = dataQuery.order('title', { ascending: true })
            break
          case 'rating':
            dataQuery = dataQuery.order('rating_kp', {
              ascending: false,
              nullsFirst: false,
            })
            break
          default:
            // "random" — fetch by id, shuffle client-side
            dataQuery = dataQuery.order('id', { ascending: true })
            break
        }

        // Apply pagination
        const from = (page - 1) * PAGE_SIZE
        const to = from + PAGE_SIZE - 1
        dataQuery = dataQuery.range(from, to)

        // Execute both queries
        const [countResult, dataResult] = await Promise.all([
          countQuery,
          dataQuery,
        ])

        if (cancelled) return

        if (dataResult.error) {
          console.warn('Supabase error:', dataResult.error.message)
          setFilms(mockFilms.slice(from, to + 1))
          setTotalCount(mockFilms.length)
          setError(dataResult.error.message)
        } else if (dataResult.data && dataResult.data.length > 0) {
          let mapped = dataResult.data.map(mapRowToFilm)
          // Shuffle for default "random" sort
          if (sortBy === 'relevance') {
            mapped = shuffleArray(mapped)
          }
          setFilms(mapped)
          setTotalCount(countResult.count ?? dataResult.data.length)
        } else {
          setFilms([])
          setTotalCount(0)
        }
      } catch (err: any) {
        if (cancelled) return
        console.warn('Network error:', err)
        setFilms(mockFilms)
        setTotalCount(mockFilms.length)
        setError(err.message ?? 'Unknown error')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    fetchFilms()
    return () => {
      cancelled = true
    }
  }, [page, filters, sortBy])

  const totalPages = Math.ceil(totalCount / PAGE_SIZE)

  return { films, loading, error, totalCount, totalPages, pageSize: PAGE_SIZE }
}

// ─── Simple hook for other pages (Home, Profile) ───
export function useFilms() {
  const [films, setFilms] = useState<Film[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false

    async function fetchFilms() {
      try {
        setLoading(true)
        setError(null)

        const { data, error: dbError } = await supabase
          .from('movies')
          .select('*')
          .order('year', { ascending: true })
          .limit(20)

        if (cancelled) return

        if (dbError) {
          console.warn('Supabase error:', dbError.message)
          setFilms(mockFilms)
          setError(dbError.message)
        } else if (data && data.length > 0) {
          setFilms(shuffleArray(data.map(mapRowToFilm)))
        } else {
          setFilms(mockFilms)
        }
      } catch (err: any) {
        if (cancelled) return
        setFilms(mockFilms)
        setError(err.message ?? 'Unknown error')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    fetchFilms()
    return () => {
      cancelled = true
    }
  }, [])

  return { films, loading, error }
}

// ─── Single film hook ───
export function useFilm(id: string | undefined) {
  const [film, setFilm] = useState<Film | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false

    async function fetchFilm() {
      if (!id) {
        setFilm(null)
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        setError(null)

        const { data, error: dbError } = await supabase
          .from('movies')
          .select('*')
          .eq('id', id)
          .maybeSingle()

        if (cancelled) return

        if (dbError) {
          const mockFilm = mockFilms.find((f) => f.id === id) || mockFilms[0]
          setFilm(mockFilm)
          setError(dbError.message)
        } else if (data) {
          setFilm(mapRowToFilm(data))
        } else {
          const mockFilm = mockFilms.find((f) => f.id === id) || null
          setFilm(mockFilm)
        }
      } catch (err: any) {
        if (cancelled) return
        const mockFilm = mockFilms.find((f) => f.id === id) || mockFilms[0]
        setFilm(mockFilm)
        setError(err.message ?? 'Unknown error')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    fetchFilm()
    return () => {
      cancelled = true
    }
  }, [id])

  return { film, loading, error }
}

// ─── Search hook ───
export function useSearchFilms(query: string) {
  const [films, setFilms] = useState<Film[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false

    async function searchFilms() {
      if (!query.trim()) {
        setFilms([])
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        setError(null)

        const searchTerm = `%${query.trim()}%`

        const { data, error: dbError } = await supabase
          .from('movies')
          .select('*')
          .ilike('title', searchTerm)
          .order('year', { ascending: false })
          .limit(50)

        if (cancelled) return

        if (dbError) {
          setFilms(localSearch(query))
          setError(dbError.message)
        } else if (data && data.length > 0) {
          setFilms(data.map(mapRowToFilm))
        } else {
          setFilms(localSearch(query))
        }
      } catch (err: any) {
        if (cancelled) return
        setFilms(localSearch(query))
        setError(err.message ?? 'Unknown error')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    searchFilms()
    return () => {
      cancelled = true
    }
  }, [query])

  return { films, loading, error }
}

// ─── Similar films hook (genre-based) ───
export function useSimilarFilms(film: Film | null) {
  const [films, setFilms] = useState<Film[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    let cancelled = false

    async function fetchSimilar() {
      if (!film || film.genreTags.length === 0) {
        setFilms([])
        setLoading(false)
        return
      }

      try {
        setLoading(true)

        // Find movies that share at least one genre, exclude current film
        const { data, error: dbError } = await supabase
          .from('movies')
          .select('*')
          .overlaps('genres', film.genreTags)
          .neq('id', Number(film.id))
          .limit(20)

        if (cancelled) return

        if (dbError) {
          console.warn('Similar films error:', dbError.message)
          setFilms([])
          return
        }

        if (data && data.length > 0) {
          const mapped = data.map(mapRowToFilm)

          // Score by number of shared genres, then shuffle within same score
          const scored = mapped.map((f) => {
            const sharedGenres = f.genreTags.filter((g) =>
              film.genreTags.includes(g),
            ).length
            return { film: f, score: sharedGenres }
          })

          scored.sort((a, b) => b.score - a.score)

          // Take top 8, then pick 4 randomly for variety
          const top = scored.slice(0, 8).map((s) => s.film)
          const selected = shuffleArray(top).slice(0, 4)
          setFilms(selected)
        } else {
          setFilms([])
        }
      } catch (err) {
        if (cancelled) return
        console.warn('Similar films network error:', err)
        setFilms([])
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    fetchSimilar()
    return () => {
      cancelled = true
    }
  }, [film?.id, film?.genreTags?.join(',')])

  return { films, loading }
}

function localSearch(query: string): Film[] {
  const q = query.toLowerCase()
  return mockFilms.filter(
    (film) =>
      film.title.toLowerCase().includes(q) ||
      film.director.toLowerCase().includes(q) ||
      film.cast.some((actor) => actor.toLowerCase().includes(q)),
  )
}
